/**
 * Usage Page
 *
 * Displays comprehensive usage metrics and billing information
 */

import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@forgenetwork/ui';
import { UsageDashboard } from '@/components/billing/UsageDashboard';
import { useWorkspaces } from '@/hooks/useDashboard';
import { ArrowLeft, Download, RefreshCw } from 'lucide-react';
import { api } from '@/lib/api';
import { useToast } from '@/shared/hooks/useToast';

export const Route = createFileRoute('/_authenticated/usage')({
  component: UsagePage,
});

function UsagePage(): React.JSX.Element {
  const { toast } = useToast();
  const { data: workspaces = [], isLoading: workspacesLoading } = useWorkspaces();
  const [selectedWorkspaceId, setSelectedWorkspaceId] = React.useState<string | null>(null);
  const [isExporting, setIsExporting] = React.useState(false);

  // Auto-select first workspace
  React.useEffect(() => {
    const firstWorkspace = workspaces[0];
    if (firstWorkspace && !selectedWorkspaceId) {
      setSelectedWorkspaceId(firstWorkspace.id);
    }
  }, [workspaces, selectedWorkspaceId]);

  const handleExportUsage = async () => {
    if (!selectedWorkspaceId) {
      toast({
        title: 'No Workspace Selected',
        description: 'Please select a workspace to export usage data.',
        variant: 'destructive',
      });
      return;
    }

    setIsExporting(true);
    try {
      const response = await api.download(
        `/usage/export?workspaceId=${selectedWorkspaceId}&format=csv&period=month`
      );

      // api.download handles response.ok check internally

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch?.[1] || `usage-export-${new Date().toISOString().split('T')[0]}.csv`;

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: 'Export Successful',
        description: `Usage data exported as ${filename}`,
      });
    } catch (error) {
      toast({
        title: 'Export Failed',
        description: error instanceof Error ? error.message : 'Failed to export usage data',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const selectedWorkspace = workspaces.find((w) => w.id === selectedWorkspaceId);

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
        </div>

        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Usage & Billing</h1>
            <p className="text-muted-foreground mt-2">
              Monitor your LLM token usage and spending across all models
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <Button variant="outline" size="sm" onClick={handleExportUsage} disabled={isExporting || !selectedWorkspaceId}>
              <Download className="h-4 w-4 mr-2" />
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
          </div>
        </div>
      </div>

      {/* Workspace Selector */}
      {workspaces.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Workspace</CardTitle>
            <CardDescription>
              Select a workspace to view its usage metrics
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select
              value={selectedWorkspaceId || undefined}
              onValueChange={setSelectedWorkspaceId}
              disabled={workspacesLoading}
            >
              <SelectTrigger className="w-full md:w-[300px]">
                <SelectValue placeholder="Select workspace" />
              </SelectTrigger>
              <SelectContent>
                {workspaces.map((workspace) => (
                  <SelectItem key={workspace.id} value={workspace.id}>
                    {workspace.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedWorkspace && (
              <p className="text-sm text-muted-foreground mt-2">
                {selectedWorkspace.description || 'No description'}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Usage Dashboard */}
      {selectedWorkspaceId ? (
        <UsageDashboard workspaceId={selectedWorkspaceId} />
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <RefreshCw className="w-12 h-12 mx-auto mb-4 opacity-50 animate-pulse" />
              <p>Select a workspace to view usage metrics</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Help Section */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>About Usage Tracking</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Usage metrics are updated in real-time and tracked per billing period (monthly).
          </p>
          <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
            <li>
              <strong>Tokens:</strong> Input and output tokens consumed by LLM requests
            </li>
            <li>
              <strong>Cost:</strong> Estimated cost based on model pricing
            </li>
            <li>
              <strong>Models:</strong> Different AI models used (Claude, GPT-4, etc.)
            </li>
          </ul>
          <div className="pt-4">
            <Link to="/billing">
              <Button variant="link" className="px-0">
                View billing settings →
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
